import "extended-proptypes/lib/extend-from-standalone";
import "@web-monorepo/polyfills";
import { setLogEventFunction } from "@web-monorepo/utilities";
import { AppTopLevelErrorBoundary } from "@web-monorepo/app-shell";
import { ErrorBannerContainer } from "@web-monorepo/molecules";
import { UserActionsHistory } from "@web-monorepo/telemetry/userActionsHistory";
import { useEffect, useMemo, type ReactNode } from "react";
import { createWebErrorHandler, ignorableToolkitErrors } from "src/toolkitErrorHandling";
import AppRouter from "./AppRouter";
import * as Telemetry from "@web-monorepo/telemetry";
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";
import { DDSProvider, DesignSystemVariables } from "@web-monorepo/dds";
import { ReactQueryProvider } from "@web-monorepo/fetchers";
import { useOnFirstRender } from "@web-monorepo/hooks";
import { logToolkitEvent, useToolkitLogger } from "src/pods/logs/useToolkitLogger";
import { useToolkitSession } from "src/pods/auth/useToolkitSession";
import { initToolkitActionBus } from "src/state/toolkitReduxStore";
import ToolkitRuntime from "src/state/toolkitRuntime";
import I18NContainer from "src/I18NContainer";
import { toolkitActions } from "src/state/toolkitStore";

if (Config.nodeEnv === "production") {
  Telemetry.init({
    // Two independent paths to the same build-time APP_VERSION: the index.html `%APP_VERSION%`
    // substitution and the `Config` build define. Without a release Sentry silently discards
    // every release-health session (WEBP-523).
    release: Telemetry.resolveRelease(window.appVersion, Config.appVersion),
    dsn: "https://e55ac9522b26046ee9c90f4d3cc0b5e2@sentry.classdojo.com/12",
    allowUrls: [/https:\/\/([\w-]+\.)*classdojo\.(com|test)(:\d+)?\/(.*\/)?[^\/]+\.js/],
    tracing: {
      reactRouter: {
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      },
    },
  });
}

function App() {
  const { errorHandler, initialUrl } = useMemo(() => {
    const initialUrl = window.location.href;

    const userActionsHistory = new UserActionsHistory();
    userActionsHistory.listenToBrowserUrlChanges();

    const errorHandler = createWebErrorHandler(userActionsHistory);

    initToolkitActionBus();

    return { errorHandler, initialUrl };
  }, []);

  return (
    <div>
      <ReactQueryProvider>
        <ToolkitRuntime />
        <DDSProvider>
          <DesignSystemVariables theme="light" />
          <I18nSetup>
            <ErrorBannerContainer initialUrl={initialUrl} />
            <AppTopLevelErrorBoundary errorHandler={errorHandler} excludeErrors={ignorableToolkitErrors}>
              <AppRouter />
            </AppTopLevelErrorBoundary>
          </I18nSetup>
        </DDSProvider>
      </ReactQueryProvider>
    </div>
  );
}

export default App;

function I18nSetup({ children }: { children: ReactNode }) {
  const { user } = useToolkitSession();
  useToolkitLogger();
  const locale = user?.locale;

  useOnFirstRender(() => {
    setLogEventFunction(async (...args) => logToolkitEvent(...args));

    toolkitActions.setInteractable(true);
  });

  return <I18NContainer locale={locale ?? null}>{children}</I18NContainer>;
}
